import { effect, Injectable, computed, inject, signal } from '@angular/core';
import { ActivityAppsService } from '../../timesheet-final/services/activity-apps.service';
import { TimesheetService } from '../../timesheet-final/services/timesheet.service';
import { HttpStatusCode } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { SnackbarService } from '../../../services/snack-service/snackbar-service';
import type {
  ApiAssignedTask,
  ApiEmployee,
  ApiHistoryEntry,
  ApiUnreportedApp,
  ApprovalStatus,
  DayStatusMap,
  FilterCounts,
  FilterTab,
  ProjectMemberStatusRowDto,
  RejectionMeta,
  TaskStatus,
  UiAssignedTask,
  UiEmployee,
  UiTimesheetEntry,
  ViewMode,
  WeeklyProjectGroup,
  WeeklyTaskRow,
  PendingApprovalPostBody,
  ProjectTimesheetHourEdit,
  ProjectTimesheetSubmitTrailRow,
  TimesheetApprovalMutationResult,
} from '../models/pm-timesheet.models';
import { PM_REJECTION_LIMIT, pmActivityKindFromEntry } from '../models/pm-timesheet.models';
import { pmEnrichEntriesWithTaskBoardBillable } from '../utils/pm-billable.util';
import {
  PM_APPROVAL_WEEK_MIN_MONDAY_STR,
  TIMESHEET_FILL_MIN_DATE_STR,
} from '../constants';
import { PmTimesheetApiService } from './pm-timesheet-api.service';

function memberInfoFromStatusRow(row: ProjectMemberStatusRowDto) {
  return row.MemberInfo ?? row.memberInfo ?? undefined;
}

function membersStatusByMemberId(rows: ProjectMemberStatusRowDto[]): Map<string, ProjectMemberStatusRowDto> {
  const m = new Map<string, ProjectMemberStatusRowDto>();
  for (const row of rows) {
    const mi = memberInfoFromStatusRow(row);
    const id = row.memberID ?? mi?.id;
    if (id != null) m.set(String(id), row);
  }
  return m;
}

function mergeEmployeesWithMembersStatus(
  employees: ApiEmployee[],
  rows: ProjectMemberStatusRowDto[],
): ApiEmployee[] {
  const idx = membersStatusByMemberId(rows);
  return employees.map((e) => {
    const row = idx.get(e.id);
    if (!row) return e;
    const mi = memberInfoFromStatusRow(row);
    const st = (row.status ?? row.Status ?? '').trim();
    const pic = mi?.profilePic?.trim();
    return {
      ...e,
      ...(st ? { memberStatus: st } : {}),
      role: (mi?.designation && String(mi.designation).trim()) || e.role,
      ...(pic ? { profilePicUrl: pic } : {}),
      ...(row.assigned_hours != null && Number.isFinite(Number(row.assigned_hours))
        ? { assignedHours: Number(row.assigned_hours) }
        : {}),
      ...(row.count_of_task_assigned != null && Number.isFinite(Number(row.count_of_task_assigned))
        ? { countOfTaskAssigned: Number(row.count_of_task_assigned) }
        : {}),
    };
  });
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

export function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Monday as first day of week */
export function startOfWeekMonday(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() + n);
  return x;
}

export function parseIsoDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** `YYYY-MM-DD` calendar validation (rejects invalid dates like 2026-02-30). */
export function isValidIsoDateString(s: string): boolean {
  const trimmed = s.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return false;
  const d = parseIsoDate(trimmed);
  return !Number.isNaN(d.getTime()) && toIsoDate(d) === trimmed;
}

export interface PmTimesheetDeepLinkContext {
  projectId: string;
  userId: string;
  startDate: string;
  endDate: string;
  projectType?: string;
  filter?: FilterTab;
}

/** RM Review & Approval email deep link (`approval-timsheet`). */
export interface RmTimesheetDeepLinkContext {
  employeeId: string;
  startDate: string;
  endDate: string;
  projectType?: string;
}

/** Buckets for GET manager-timesheets-by-range `submitter.approvalStatus` → filter chip counts. */
function managerRangeApprovalBucket(
  status: string | undefined,
): 'pending' | 'approved' | 'rejected' | null {
  const x = (status ?? '').toLowerCase().trim();
  if (x === 'submitted' || x === 'pending' || x === 'submitted_for_approval') return 'pending';
  if (x === 'approved') return 'approved';
  if (x === 'rejected') return 'rejected';
  return null;
}

const APPROVAL_DAY_SEVERITY: Record<ApprovalStatus, number> = {
  rejected: 0,
  submitted: 1,
  draft: 2,
  approved: 3,
};

function pickStricterApprovalDayStatus(a: ApprovalStatus, b: ApprovalStatus): ApprovalStatus {
  return APPROVAL_DAY_SEVERITY[a] <= APPROVAL_DAY_SEVERITY[b] ? a : b;
}

/** Maps UI filter tab → pending-approval `status`; omit for “all”. */
function pendingApprovalStatusFromFilter(
  tab: FilterTab,
): 'submitted' | 'approved' | 'rejected' | undefined {
  if (tab === 'pending') return 'submitted';
  if (tab === 'approved') return 'approved';
  if (tab === 'rejected') return 'rejected';
  return undefined;
}

@Injectable({ providedIn: 'root' })
export class PmTimesheetService {
  private readonly api = inject(PmTimesheetApiService);
  private readonly snack = inject(SnackbarService);
  private readonly shell = inject(TimesheetService);
  private readonly activityApps = inject(ActivityAppsService);

  readonly today = signal<string>(toIsoDate(new Date()));
  readonly curDate = signal<string>(toIsoDate(new Date()));
  readonly curView = signal<ViewMode>('weekly');
  readonly activeFilter = signal<FilterTab>('all');
  readonly selectedEmpId = signal<string>('');
  readonly empSearch = signal<string>('');
  readonly spSearch = signal<string>('');
  readonly isLoading = signal<boolean>(false);
  /**
   * True when GET manager-timesheets-by-range returned `employees: []` for the visible range
   * (not falling back to project members-only list).
   */
  readonly managerTimesheetsRangeEmpty = signal<boolean>(false);
  /** Route `projectId` for manager-timesheets-by-range. */
  readonly contextProjectId = signal<string>('');
  /** Email deep link: optional project type filter (e.g. `RA`). */
  readonly contextProjectType = signal<string>('');
  /** Roster from GET reporting-manager-by-range (support reportees), not PM project members. */
  readonly reportingManagerRosterMode = signal(false);
  private rmEmpToProjectIds: Record<string, string[]> = {};
  /** Set by `bootstrapFromDeepLink` before `fetchTeamMembers` selects the employee. */
  private pendingDeepLinkUserId: string | null = null;

  constructor() {
    effect(() => {
      if (!this.reportingManagerRosterMode()) return;
      const anchor = this.shell.currentDate();
      const iso = toIsoDate(
        new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate()),
      );
      const viewChanged = this.curView() !== 'weekly';
      const dateChanged = this.curDate() !== iso;
      if (!viewChanged && !dateChanged) return;
      if (viewChanged) this.curView.set('weekly');
      if (dateChanged) this.curDate.set(iso);
      this.reloadAllVisibleEmployees();
    });

    effect(() => {
      if (!this.activityApps.loaded()) return;
      void this.activityApps.apps();
      this.entries.update((byEmp) => {
        const next: Record<string, UiTimesheetEntry[]> = {};
        for (const [empId, list] of Object.entries(byEmp)) {
          next[empId] = list.map((e) =>
            e.pmAppId != null
              ? { ...e, pmAppLabel: this.activityApps.nameFor(e.pmAppId, e.pmAppLabel ?? '') }
              : e,
          );
        }
        return next;
      });
      this.unreportedApps.update((byEmp) => {
        const next: Record<string, ApiUnreportedApp[]> = {};
        for (const [empId, list] of Object.entries(byEmp)) {
          next[empId] = list.map((a) =>
            a.appId != null ? { ...a, appName: this.activityApps.nameFor(a.appId, a.appName) } : a,
          );
        }
        return next;
      });
    });
  }

  readonly teamMembers = signal<ApiEmployee[]>([]);
  readonly entries = signal<Record<string, UiTimesheetEntry[]>>({});
  readonly dayStatus = signal<DayStatusMap>({});
  readonly rejMeta = signal<Record<string, RejectionMeta>>({});
  readonly histLog = signal<ApiHistoryEntry[]>([]);
  readonly assignedTasks = signal<Record<string, ApiAssignedTask[]>>({});
  readonly unreportedApps = signal<Record<string, ApiUnreportedApp[]>>({});
  /** GET submit-trail — approval batches for `contextProjectId` (same API response as employee submit flow). */
  readonly submitTrail = signal<ProjectTimesheetSubmitTrailRow[]>([]);

  readonly REJECTION_LIMIT = PM_REJECTION_LIMIT;

  readonly hasProjectContext = computed(() => this.contextProjectId().trim().length > 0);

  isReportingManagerRoster(): boolean {
    return this.reportingManagerRosterMode();
  }

  readonly submitTrailItems = computed(() => {
    const team = this.teamMembers();
    const nameById = new Map(team.map((e) => [e.id, e.name]));
    const rows = [...this.submitTrail()].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
    return rows.map((row) => ({
      row,
      submitterName: row.submitterName?.trim() || nameById.get(String(row.userId)) || `User ${row.userId}`,
    }));
  });

  /** Approval trail rows limited to the currently selected employee. */
  readonly selectedEmpSubmitTrailItems = computed(() => {
    const selectedId = this.selectedEmpId().trim();
    if (!selectedId) return [];
    return this.submitTrailItems().filter((item) => String(item.row.userId) === selectedId);
  });

  readonly visibleDates = computed<string[]>(() => {
    const mode = this.reportingManagerRosterMode() ? 'weekly' : this.curView();
    const cur = parseIsoDate(this.curDate());
    if (mode === 'daily') {
      return [this.curDate()];
    }
    const start = startOfWeekMonday(cur);
    return Array.from({ length: 7 }, (_, i) => toIsoDate(addDays(start, i)));
  });

  readonly weekLabel = computed<string>(() => {
    const dates = this.visibleDates();
    if (!dates.length) return '';
    const a = parseIsoDate(dates[0]);
    const b = parseIsoDate(dates[dates.length - 1]);
    const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    if (dates.length === 1) {
      return a.toLocaleDateString('en-GB', opts);
    }
    const left = a.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const right = b.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${left} – ${right}`;
  });

  /** Top bar pill: matches employee timesheet (e.g. Mar 16 - Mar 22, 2026). */
  readonly topbarDateLabel = computed<string>(() => {
    const dates = this.visibleDates();
    if (!dates.length) return '';
    const a = parseIsoDate(dates[0]);
    const b = parseIsoDate(dates[dates.length - 1]);
    const one: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    if (dates.length === 1) {
      return a.toLocaleDateString('en-US', one);
    }
    const left = a.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const right = b.toLocaleDateString('en-US', one);
    return `${left} - ${right}`;
  });

  /** PM top bar date pill: daily = weekday date; weekly = compact range (image ref). */
  readonly navPillLabel = computed<string>(() => {
    if (this.curView() === 'daily') {
      const d = parseIsoDate(this.curDate());
      return d.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }
    const dates = this.visibleDates();
    if (!dates.length) return '';
    const a = parseIsoDate(dates[0]);
    const b = parseIsoDate(dates[dates.length - 1]);
    const one: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
    const left = a.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const right = b.toLocaleDateString('en-US', one);
    return `${left} – ${right}`;
  });

  readonly employees = computed<UiEmployee[]>(() =>
    this.teamMembers().map((e) => this.enrichEmployee(e)),
  );

  /** Counts for top chips — from manager-timesheets-by-range `submitter.approvalStatus` per team row. */
  readonly filterCounts = computed<FilterCounts>(() => {
    const list = this.teamMembers();
    const counts: FilterCounts = { all: 0, pending: 0, approved: 0, rejected: 0 };
    counts.all = list.length;
    for (const emp of list) {
      const bucket = managerRangeApprovalBucket(emp.approvalStatus);
      if (bucket === 'pending') counts.pending++;
      else if (bucket === 'approved') counts.approved++;
      else if (bucket === 'rejected') counts.rejected++;
    }
    return counts;
  });

  /** Status tab counts for the weekly grid (stable roster counts while entries refetch per tab). */
  readonly weeklyGridFilterCounts = computed<FilterCounts>(() => this.filterCounts());

  readonly filteredEmployees = computed<UiEmployee[]>(() => {
    const q = this.empSearch().trim().toLowerCase();
    const tab = this.activeFilter();
    const apiSt = (s: string | undefined) => (s ?? '').toLowerCase();
    return this.employees().filter((e) => {
      if (
        q &&
        !e.name.toLowerCase().includes(q) &&
        !e.role.toLowerCase().includes(q) &&
        !(e.memberStatus?.toLowerCase().includes(q) ?? false)
      ) {
        return false;
      }
      if (tab === 'all') return true;
      const st = apiSt(e.approvalStatus);
      if (tab === 'pending') {
        return e.pendingCount > 0 || st === 'submitted' || st === 'pending';
      }
      if (tab === 'approved') {
        return e.approvedCount > 0 || st === 'approved';
      }
      if (tab === 'rejected') {
        return e.rejectedCount > 0 || st === 'rejected';
      }
      return true;
    });
  });

  readonly selectedEmployee = computed<UiEmployee | undefined>(() =>
    this.employees().find((e) => e.id === this.selectedEmpId()),
  );

  readonly selectedEntries = computed<UiTimesheetEntry[]>(() => {
    const empId = this.selectedEmpId();
    if (!empId) return [];
    const list = this.entries()[empId] ?? [];
    const allowed = new Set(this.visibleDates());
    return list.filter((e) => allowed.has(e.date));
  });

  /** Entries shown in the weekly grid (RM: filtered server-side via pending-approval `status`). */
  readonly weeklyGridEntries = computed<UiTimesheetEntry[]>(() => this.selectedEntries());

  readonly weeklyTaskRows = computed<WeeklyTaskRow[]>(() => {
    const dates = this.visibleDates();
    const byKey = new Map<
      string,
      Omit<WeeklyTaskRow, 'weekTotal'>
    >();
    for (const e of this.weeklyGridEntries()) {
      const kind = pmActivityKindFromEntry(e);
      const appKey = e.pmAppId != null ? String(e.pmAppId) : 'na';
      const key = `${e.projectId}|${kind}|${e.task}|${appKey}`;
      let row = byKey.get(key);
      if (!row) {
        row = {
          projectId: e.projectId,
          taskName: e.task,
          path: '',
          activityKind: kind,
          appLabel: e.pmAppLabel ?? '',
          projColor: e.projectColor,
          projName: e.projectName,
          ...(e.projectCode ? { projectCode: e.projectCode } : {}),
          ...(e.projectType ? { projectType: e.projectType } : {}),
          entriesByDate: {},
        };
        byKey.set(key, row);
      }
      const arr = row.entriesByDate[e.date] ?? [];
      arr.push(e);
      row.entriesByDate[e.date] = arr;
    }
    return [...byKey.values()].map((r) => ({
      ...r,
      weekTotal: dates.reduce((s, d) => {
        const es = r.entriesByDate[d] ?? [];
        return s + es.reduce((a, x) => a + x.hours, 0);
      }, 0),
    }));
  });

  readonly weeklyProjectGroups = computed<WeeklyProjectGroup[]>(() => {
    const rows = this.weeklyTaskRows();
    const map = new Map<string, WeeklyProjectGroup>();
    for (const r of rows) {
      let g = map.get(r.projectId);
      if (!g) {
        g = {
          projectId: r.projectId,
          projectName: r.projName,
          projectColor: r.projColor,
          projectCode: r.projectCode ?? r.projectId,
          ...(r.projectType ? { projectType: r.projectType } : {}),
          tasks: [],
          projectWeekTotal: 0,
        };
        map.set(r.projectId, g);
      }
      g.tasks.push(r);
      g.projectWeekTotal += r.weekTotal;
    }
    const groups = [...map.values()];
    const typeFilter = this.contextProjectType().trim().toUpperCase();
    const pidFilter = this.contextProjectId().trim();
    if (!typeFilter && !pidFilter) return groups;
    const filtered = groups.filter((g) => {
      if (pidFilter && String(g.projectId) === pidFilter) return true;
      if (typeFilter && (g.projectType ?? '').trim().toUpperCase() === typeFilter) return true;
      return false;
    });
    if (filtered.length > 0) return filtered;
    if (pidFilter) {
      return groups.filter((g) => String(g.projectId) === pidFilter);
    }
    return groups;
  });

  /** Total logged hours for selected employee in visible range. */
  readonly selectedWeekLoggedHours = computed<number>(() =>
    this.selectedEntries().reduce((s, e) => s + e.hours, 0),
  );

  /** Logged hours for selected employee on current day (daily view). */
  readonly selectedDayLoggedHours = computed<number>(() => {
    const id = this.selectedEmpId();
    const d = this.curDate();
    if (!id) return 0;
    return (this.entries()[id] ?? []).filter((e) => e.date === d).reduce((s, e) => s + e.hours, 0);
  });

  /** History rows for selected employee + current date (daily approval trail). */
  readonly dailyApprovalHistory = computed(() => {
    const id = this.selectedEmpId();
    const d = this.curDate();
    if (!id) return [];
    return this.histLog().filter((h) => h.empId === id && h.dateStr === d);
  });

  /** History for visible week + selected employee (weekly view footer). */
  readonly weeklyApprovalHistory = computed<ApiHistoryEntry[]>(() => {
    const id = this.selectedEmpId();
    if (!id) return [];
    const allowed = new Set(this.visibleDates());
    return this.histLog()
      .filter((h) => h.empId === id && allowed.has(h.dateStr))
      .slice()
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  });

  /** Dates in visible range that are submitted (pending approval), not future, and PM may act on. */
  readonly pendingSubmittedDates = computed<string[]>(() => {
    const id = this.selectedEmpId();
    if (!id) return [];
    const todayStr = this.today();
    const st = this.dayStatus()[id] ?? {};
    return this.visibleDates().filter(
      (d) => d <= todayStr && st[d] === 'submitted' && this.canPmApproveOrRejectDate(d),
    );
  });

  /** True when the visible week’s Monday is before the PM approval cutoff (4 May 2026). */
  readonly isVisibleWeekBeforePmApprovalCutoff = computed(() => {
    const dates = this.visibleDates();
    if (!dates.length) return false;
    const weekMon = this.curView() === 'weekly' ? dates[0] : toIsoDate(startOfWeekMonday(parseIsoDate(dates[0])));
    return weekMon < PM_APPROVAL_WEEK_MIN_MONDAY_STR;
  });

  /**
   * When true, hide/disable “Edit Timesheet” for the current selection:
   * - Weekly: every non-future visible day is `approved`
   * - Daily: selected day (`curDate`) is `approved`
   */
  readonly isEditTimesheetBlockedByApproval = computed(() => {
    const id = this.selectedEmpId();
    if (!id) return false;
    const todayStr = this.today();
    if (this.curView() === 'weekly') {
      const days = this.visibleDates().filter((d) => d <= todayStr);
      if (days.length === 0) return false;
      const st = this.dayStatus()[id] ?? {};
      return days.every((d) => (st[d] ?? 'draft') === 'approved');
    }
    return this.getDayStatus(id, this.curDate()) === 'approved';
  });

  /**
   * Week is treated as rejected if any visible day is rejected in `dayStatus`, or the
   * manager-range row for this employee is `approvalStatus === 'rejected'` (API week status).
   */
  readonly hasRejectedInVisibleRange = computed(() => {
    const id = this.selectedEmpId();
    if (!id) return false;
    const st = this.dayStatus()[id] ?? {};
    if (this.visibleDates().some((d) => st[d] === 'rejected')) return true;
    const emp = this.teamMembers().find((e) => e.id === id);
    const api = (emp?.approvalStatus ?? '').toLowerCase().trim();
    return api === 'rejected';
  });

  readonly selectedUnreported = computed<ApiUnreportedApp[]>(() => {
    const empId = this.selectedEmpId();
    const apps = empId ? (this.unreportedApps()[empId] ?? []) : [];
    const q = this.spSearch().toLowerCase().trim();
    if (!q) return apps;
    return apps.filter(
      (a) => a.appName.toLowerCase().includes(q) || a.title.toLowerCase().includes(q),
    );
  });

  readonly unreportedTotal = computed<number>(() =>
    this.selectedUnreported().reduce((s, a) => s + a.hours, 0),
  );

  /** Latest rejection reason in visible range for selected employee (notice bar). */
  readonly selectionRejectionNote = computed<string>(() => {
    const id = this.selectedEmpId();
    if (!id) return '';
    const ds = this.visibleDates();
    const st = this.dayStatus();
    const rm = this.rejMeta();
    for (const d of ds) {
      const s = st[id]?.[d] ?? 'draft';
      if (s === 'rejected') {
        const n = rm[`${id}__${d}`]?.note ?? '';
        if (n) return n;
      }
    }
    return '';
  });

  /** Daily view: group entries by project then task */
  readonly dailyProjectGroups = computed(() => {
    const dateStr = this.curDate();
    const list = (this.entries()[this.selectedEmpId()] ?? []).filter((e) => e.date === dateStr);
    const byProj = new Map<
      string,
      { projName: string; projColor: string; tasks: Map<string, UiTimesheetEntry[]> }
    >();
    for (const e of list) {
      let p = byProj.get(e.projectId);
      if (!p) {
        p = { projName: e.projectName, projColor: e.projectColor, tasks: new Map() };
        byProj.set(e.projectId, p);
      }
      const kind = pmActivityKindFromEntry(e);
      const appKey = e.pmAppId != null ? String(e.pmAppId) : 'na';
      const tkey = `${kind}|${appKey}|${e.task}`;
      const arr = p.tasks.get(tkey) ?? [];
      arr.push(e);
      p.tasks.set(tkey, arr);
    }
    return [...byProj.entries()].map(([projectId, v]) => ({
      projectId,
      projectName: v.projName,
      projectColor: v.projColor,
      taskGroups: [...v.tasks.entries()].map(([key, entries]) => {
        const parts = key.split('|');
        const task = parts.length >= 3 ? parts.slice(2).join('|') : '';
        const first = entries[0];
        return {
          activityKind: pmActivityKindFromEntry(first!),
          appLabel: first?.pmAppLabel ?? '',
          task,
          entries,
          totalHrs: entries.reduce((s, x) => s + x.hours, 0),
        };
      }),
      totalHrs: [...v.tasks.values()].flat().reduce((s, x) => s + x.hours, 0),
    }));
  });

  loadInitialData(projectId: string): void {
    this.reportingManagerRosterMode.set(false);
    this.rmEmpToProjectIds = {};
    this.pendingDeepLinkUserId = null;
    this.contextProjectType.set('');
    this.contextProjectId.set(projectId);
    this.fetchTeamMembers();
  }

  /**
   * Email / shareable deep link bootstrap for project-timesheet tab.
   * Expects `startDate`/`endDate` as a Mon–Sun week; anchors `curDate` to `startDate`.
   */
  bootstrapFromDeepLink(ctx: PmTimesheetDeepLinkContext): void {
    const projectId = ctx.projectId.trim();
    const userId = ctx.userId.trim();
    const startDate = ctx.startDate.trim();
    const endDate = ctx.endDate.trim();

    if (!projectId || !userId) {
      if (projectId) this.loadInitialData(projectId);
      return;
    }

    if (!isValidIsoDateString(startDate) || !isValidIsoDateString(endDate)) {
      this.snack.show(
        'Invalid link',
        'Timesheet date range in this link is invalid. Showing the current week.',
        HttpStatusCode.BadRequest,
      );
      this.loadInitialData(projectId);
      return;
    }

    if (startDate > endDate) {
      this.snack.show(
        'Invalid link',
        'Timesheet date range in this link is invalid. Showing the current week.',
        HttpStatusCode.BadRequest,
      );
      this.loadInitialData(projectId);
      return;
    }

    this.reportingManagerRosterMode.set(false);
    this.rmEmpToProjectIds = {};
    this.contextProjectId.set(projectId);
    this.contextProjectType.set((ctx.projectType ?? '').trim());
    if (ctx.filter) {
      this.activeFilter.set(ctx.filter);
    }
    this.curView.set('weekly');
    this.curDate.set(startDate);
    this.pendingDeepLinkUserId = userId;
    this.fetchTeamMembers();
  }

  /** Reporting manager approval tab — support-project reportees (no single project context). */
  loadReportingManagerRosterData(): void {
    this.pendingDeepLinkUserId = null;
    this.curView.set('weekly');
    this.syncAnchorFromShell();
    this.contextProjectId.set('');
    this.contextProjectType.set('');
    this.reportingManagerRosterMode.set(true);
    this.fetchReportingManagerRoster();
  }

  /**
   * Email deep link for RM approval tab.
   * Anchors week to `startDate` (Monday), then selects `employeeId` after roster loads.
   * Idempotent when the same week/employee is already pending or selected.
   */
  bootstrapReportingManagerFromDeepLink(ctx: RmTimesheetDeepLinkContext): void {
    const employeeId = ctx.employeeId.trim();
    const startDate = ctx.startDate.trim();
    const endDate = ctx.endDate.trim();

    if (!employeeId) {
      this.loadReportingManagerRosterData();
      return;
    }

    if (!isValidIsoDateString(startDate) || !isValidIsoDateString(endDate) || startDate > endDate) {
      this.snack.show(
        'Invalid link',
        'Timesheet date range in this link is invalid. Showing the current week.',
        HttpStatusCode.BadRequest,
      );
      this.loadReportingManagerRosterData();
      return;
    }

    const alreadyAnchored =
      this.reportingManagerRosterMode() &&
      this.curDate() === startDate &&
      this.curView() === 'weekly';
    if (alreadyAnchored) {
      if (this.pendingDeepLinkUserId === employeeId) {
        return;
      }
      if (this.selectedEmpId() === employeeId && !this.pendingDeepLinkUserId) {
        this.contextProjectType.set((ctx.projectType ?? '').trim());
        return;
      }
    }

    this.curView.set('weekly');
    this.curDate.set(startDate);
    this.shell.switchView('weekly');
    this.shell.setAnchorFromCalendar(new Date(`${startDate}T12:00:00`));
    this.contextProjectId.set('');
    this.contextProjectType.set((ctx.projectType ?? '').trim());
    this.reportingManagerRosterMode.set(true);
    this.pendingDeepLinkUserId = employeeId;
    this.fetchReportingManagerRoster();
  }

  /** Align RM approval grid with `ts-topbar` week anchor (`TimesheetService.currentDate`). */
  private syncAnchorFromShell(): void {
    const anchor = this.shell.currentDate();
    this.curDate.set(
      toIsoDate(new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate())),
    );
  }

  /** Refetch submit trail after PM approve/reject or other project-timesheet status changes. */
  private refreshSubmitTrail(): void {
    const projectId = this.contextProjectId().trim();
    if (!projectId) return;
    this.api
      .getSubmitTrail(projectId)
      .pipe(take(1))
      .subscribe({
        next: (rows) => this.submitTrail.set(rows),
        error: () => {
          /* keep existing trail to avoid empty flash */
        },
      });
  }

  /**
   * Re-sync sidebar totals (GET manager-timesheets-by-range) and entry lines (pending-approval)
   * after PM approve/reject. Omits the full loading gate from `fetchTeamMembers` to avoid flicker.
   */
  private refreshRosterAndEmployeeAfterPmAction(empId: string): void {
    if (this.reportingManagerRosterMode()) {
      this.refreshReportingManagerRosterAfterPmAction(empId);
      return;
    }
    const projectId = this.contextProjectId().trim();
    if (!projectId) {
      this.loadEmployeeData(empId);
      return;
    }
    const dates = this.visibleDates();
    const from = dates[0];
    const to = dates[dates.length - 1];
    if (!from || !to) {
      this.loadEmployeeData(empId);
      return;
    }
    this.api
      .getTeamMembers(projectId, from, to)
      .pipe(take(1))
      .subscribe({
        next: (team) => {
          if (team.employees.length === 0) {
            this.managerTimesheetsRangeEmpty.set(true);
            this.teamMembers.set([]);
            this.selectedEmpId.set('');
            return;
          }
          this.managerTimesheetsRangeEmpty.set(false);
          const prevId = this.selectedEmpId();
          this.teamMembers.set(team.employees);
          const keep = !!(prevId && team.employees.some((e) => e.id === prevId));
          this.selectedEmpId.set(keep ? prevId! : team.employees[0]!.id);
          this.api
            .getProjectMembersStatus(projectId)
            .pipe(take(1))
            .subscribe({
              next: (membersStatus) => {
                this.teamMembers.set(mergeEmployeesWithMembersStatus(team.employees, membersStatus));
              },
            });
          team.employees.forEach((e) => this.loadEmployeeData(e.id));
        },
        error: () => {
          this.loadEmployeeData(empId);
        },
      });
  }

  /** RM approval tab: refresh reportee roster so filter chip counts match backend after approve/reject. */
  private refreshReportingManagerRosterAfterPmAction(empId: string): void {
    const dates = this.visibleDates();
    const from = dates[0];
    const to = dates[dates.length - 1];
    if (!from || !to) {
      this.loadEmployeeData(empId);
      return;
    }
    this.api
      .getReportingManagerReporteesByRange(from, to)
      .pipe(take(1))
      .subscribe({
        next: (team) => {
          this.rmEmpToProjectIds = team.empToProjectIds ?? {};
          const rangeEmpty = team.employees.length === 0;
          this.managerTimesheetsRangeEmpty.set(rangeEmpty);
          if (rangeEmpty) {
            this.teamMembers.set([]);
            this.selectedEmpId.set('');
            return;
          }
          const prevId = this.selectedEmpId();
          this.teamMembers.set(team.employees);
          const keep = !!(prevId && team.employees.some((e) => e.id === prevId));
          const nextSelected = keep ? prevId! : team.employees[0]!.id;
          this.selectedEmpId.set(nextSelected);
          this.loadEmployeeData(nextSelected);
        },
        error: () => {
          this.loadEmployeeData(empId);
        },
      });
  }

  private fetchReportingManagerRoster(): void {
    const dates = this.visibleDates();
    const from = dates[0];
    const to = dates[dates.length - 1];
    if (!from || !to) return;
    this.isLoading.set(true);
    this.managerTimesheetsRangeEmpty.set(false);
    this.api
      .getReportingManagerReporteesByRange(from, to)
      .pipe(take(1))
      .subscribe({
        next: (team) => {
          this.rmEmpToProjectIds = team.empToProjectIds ?? {};
          const rangeEmpty = team.employees.length === 0;
          this.managerTimesheetsRangeEmpty.set(rangeEmpty);
          const deepLinkUserId = this.pendingDeepLinkUserId;
          this.pendingDeepLinkUserId = null;
          if (rangeEmpty) {
            this.teamMembers.set([]);
            this.selectedEmpId.set('');
            if (deepLinkUserId) {
              this.snack.show(
                'Employee not found',
                'No reportees were returned for this week. The employee in this link may not be in your roster.',
                HttpStatusCode.NotFound,
              );
            }
            this.isLoading.set(false);
            return;
          }
          const prevId = this.selectedEmpId();
          this.teamMembers.set(team.employees);
          if (deepLinkUserId) {
            const match = team.employees.find((e) => String(e.id) === String(deepLinkUserId));
            if (match) {
              this.selectedEmpId.set(match.id);
            } else {
              this.snack.show(
                'Employee not found',
                'The employee in this link is not in your roster for the selected week. Showing the first reportee.',
                HttpStatusCode.NotFound,
              );
              this.selectedEmpId.set(team.employees[0]!.id);
            }
          } else {
            const keep = !!(prevId && team.employees.some((e) => e.id === prevId));
            this.selectedEmpId.set(keep ? prevId : team.employees[0]!.id);
          }
          const selectedId = this.selectedEmpId();
          if (selectedId) this.loadEmployeeData(selectedId);
          this.isLoading.set(false);
        },
        error: () => {
          this.pendingDeepLinkUserId = null;
          this.managerTimesheetsRangeEmpty.set(false);
          this.isLoading.set(false);
        },
      });
  }

  private buildPendingApprovalPostBody(
    userNum: number,
    from: string,
    to: string,
    projectId: number,
  ): PendingApprovalPostBody {
    const status = pendingApprovalStatusFromFilter(this.activeFilter());
    return {
      userId: userNum,
      startDate: from,
      endDate: to,
      projectId,
      ...(status ? { status } : {}),
    };
  }

  /** GET manager-timesheets-by-range for visible start/end (weekly = Mon–Sun). */
  private fetchTeamMembers(): void {
    const projectId = this.contextProjectId();
    if (!projectId) return;
    const dates = this.visibleDates();
    const from = dates[0];
    const to = dates[dates.length - 1];
    if (!from || !to) return;
    this.isLoading.set(true);
    this.managerTimesheetsRangeEmpty.set(false);
    this.api
      .getTeamMembers(projectId, from, to)
      .pipe(take(1))
      .subscribe({
        next: (team) => {
          const rangeEmpty = team.employees.length === 0;
          this.managerTimesheetsRangeEmpty.set(rangeEmpty);
          const deepLinkUserId = this.pendingDeepLinkUserId;
          this.pendingDeepLinkUserId = null;
          if (rangeEmpty) {
            this.teamMembers.set([]);
            this.selectedEmpId.set('');
            if (deepLinkUserId) {
              this.snack.show(
                'Employee not found',
                'No timesheets were returned for this week. The employee in this link may not exist on this project.',
                HttpStatusCode.NotFound,
              );
            }
            this.api
              .getSubmitTrail(projectId)
              .pipe(take(1))
              .subscribe({
                next: (submitTrail) => {
                  this.submitTrail.set(submitTrail);
                  this.isLoading.set(false);
                },
                error: () => {
                  this.submitTrail.set([]);
                  this.isLoading.set(false);
                },
              });
            return;
          }

          const prevId = this.selectedEmpId();
          this.teamMembers.set(team.employees);

          if (deepLinkUserId) {
            const match = team.employees.find((e) => String(e.id) === String(deepLinkUserId));
            if (match) {
              this.selectedEmpId.set(match.id);
            } else {
              this.snack.show(
                'Employee not found',
                'The employee in this link is not on this project for the selected week. Showing the first team member.',
                HttpStatusCode.NotFound,
              );
              this.selectedEmpId.set(team.employees[0]!.id);
            }
            team.employees.forEach((e) => this.loadEmployeeData(e.id));
          } else {
            const keep = !!(prevId && team.employees.some((e) => e.id === prevId));
            this.selectedEmpId.set(keep ? prevId : team.employees[0]!.id);
            team.employees.forEach((e) => this.loadEmployeeData(e.id));
          }
          this.isLoading.set(false);

          this.api
            .getProjectMembersStatus(projectId)
            .pipe(take(1))
            .subscribe({
              next: (membersStatus) => {
                this.teamMembers.set(mergeEmployeesWithMembersStatus(team.employees, membersStatus));
              },
            });
          this.api
            .getSubmitTrail(projectId)
            .pipe(take(1))
            .subscribe({
              next: (submitTrail) => this.submitTrail.set(submitTrail),
              error: () => this.submitTrail.set([]),
            });
        },
        error: () => {
          this.submitTrail.set([]);
          this.managerTimesheetsRangeEmpty.set(false);
          this.isLoading.set(false);
        },
      });
  }

  /** RM Approval tab: merge POST support-project-timesheets/pending-approval per assigned project. */
  private supportPendingApprovalEntries$(
    userNum: number,
    from: string,
    to: string,
    empId: string,
  ): Observable<{ entries: UiTimesheetEntry[]; dayStatuses: Record<string, ApprovalStatus> }> {
    const projectIds = (this.rmEmpToProjectIds[empId] ?? [])
      .map((id) => Number(id))
      .filter((n) => Number.isFinite(n) && n > 0);
    const uniqueIds = [...new Set(projectIds)];
    const statusBody = this.buildPendingApprovalPostBody(userNum, from, to, 0);
    if (uniqueIds.length === 0) {
      return this.api
        .postSupportProjectPendingApprovalTimesheet(statusBody)
        .pipe(
          map((r) => ({
            entries: r.entries as UiTimesheetEntry[],
            dayStatuses: r.dayStatuses,
          })),
        );
    }
    return forkJoin(
      uniqueIds.map((projectId) =>
        this.api
          .postSupportProjectPendingApprovalTimesheet(
            this.buildPendingApprovalPostBody(userNum, from, to, projectId),
          )
          .pipe(
            map((r) => ({
              entries: this.stampSupportEntriesProjectId(
                r.entries as UiTimesheetEntry[],
                projectId,
              ),
              dayStatuses: r.dayStatuses,
            })),
          ),
      ),
    ).pipe(
      map((results) => {
        const entries: UiTimesheetEntry[] = [];
        const dayStatuses: Record<string, ApprovalStatus> = {};
        for (const r of results) {
          entries.push(...r.entries);
          for (const [d, st] of Object.entries(r.dayStatuses)) {
            const prev = dayStatuses[d];
            dayStatuses[d] =
              prev == null ? st : pickStricterApprovalDayStatus(prev, st);
          }
        }
        return { entries, dayStatuses };
      }),
    );
  }

  /** RM pending-approval is fetched per project; keep row `projectId` aligned with the request. */
  private stampSupportEntriesProjectId(
    entries: UiTimesheetEntry[],
    projectId: number,
  ): UiTimesheetEntry[] {
    const pid = String(projectId);
    return entries.map((e) => ({ ...e, projectId: pid }));
  }

  private parsePositiveProjectId(raw: string | number | undefined): number | null {
    const n = typeof raw === 'number' ? raw : Number(String(raw ?? '').trim());
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * Project ids for support approval APIs: roster mapping from reporting-manager-by-range,
   * optionally narrowed to projects that have lines on the given day(s).
   */
  private resolveSupportApprovalProjectIds(
    empId: string,
    opts?: { dateStr?: string; pendingDates?: string[] },
  ): number[] {
    const ids = new Set<number>();
    for (const raw of this.rmEmpToProjectIds[empId] ?? []) {
      const n = this.parsePositiveProjectId(raw);
      if (n != null) ids.add(n);
    }
    const pendingSet = opts?.pendingDates?.length
      ? new Set(opts.pendingDates)
      : null;
    const dateFilter = opts?.dateStr?.trim();
    for (const e of this.entries()[empId] ?? []) {
      if (dateFilter && e.date !== dateFilter) continue;
      if (pendingSet && !pendingSet.has(e.date)) continue;
      const n = this.parsePositiveProjectId(e.projectId);
      if (n != null) ids.add(n);
    }
    return [...ids];
  }

  private postSupportApprovalStatusByRangeForProjects(
    projectIds: number[],
    userId: number,
    startDate: string,
    endDate: string,
    approvalStatus: 'approved' | 'rejected',
    remark: string,
  ): Observable<TimesheetApprovalMutationResult> {
    if (!projectIds.length || !Number.isFinite(userId) || userId <= 0) {
      return of({ success: false, message: 'Could not resolve support project for approval.' });
    }
    return forkJoin(
      projectIds.map((projectId) =>
        this.api.postSupportProjectTimesheetApprovalStatusByRange({
          userId,
          projectId,
          startDate,
          endDate,
          approvalStatus,
          remark,
        }),
      ),
    ).pipe(
      map((results) => {
        const success = results.length > 0 && results.every((r) => r.success);
        const withMessage = results.find((r) => r.message.trim());
        const message =
          withMessage?.message ??
          (success ? 'Request completed successfully.' : 'Request failed.');
        return { success, message };
      }),
    );
  }

  private showApprovalMutationSnack(
    result: TimesheetApprovalMutationResult,
    successTitle: string,
  ): void {
    const code = result.success ? HttpStatusCode.Ok : HttpStatusCode.BadRequest;
    const title = result.success ? successTitle : 'Error';
    this.snack.show(title, result.message, code);
  }

  private approvalResultFromLegacyResponse(
    res: { success?: boolean; message?: string },
    fallbackSuccess: string,
    fallbackError: string,
  ): TimesheetApprovalMutationResult {
    const success = !!res?.success;
    const message = String(res?.message ?? '').trim() || (success ? fallbackSuccess : fallbackError);
    return { success, message };
  }

  private approvalResultFromHttpError(err: unknown, fallbackError: string): TimesheetApprovalMutationResult {
    const raw = (err as { error?: unknown })?.error;
    const fromBody =
      raw != null && typeof raw === 'object'
        ? String((raw as Record<string, unknown>)['message'] ?? (raw as Record<string, unknown>)['Message'] ?? '').trim()
        : '';
    return { success: false, message: fromBody || fallbackError };
  }

  private recordApprovalHistory(
    empId: string,
    dateStrs: string[],
    action: ApiHistoryEntry['action'],
    note: string,
  ): void {
    const ts = new Date().toISOString();
    const actorName = 'Current PM';
    const actorRole = 'PM';
    this.histLog.update((h) => [
      ...h,
      ...dateStrs.map((dateStr, i) => ({
        id: `local_${Date.now()}_${i}`,
        empId,
        dateStr,
        action,
        actorName,
        actorRole,
        timestamp: ts,
        note,
      })),
    ]);
  }

  private afterPmApprovalMutation(empId: string): void {
    this.refreshSubmitTrail();
    this.refreshRosterAndEmployeeAfterPmAction(empId);
  }

  loadEmployeeData(empId: string): void {
    const dates = this.visibleDates();
    const from = dates[0];
    const to = dates[dates.length - 1];
    if (!from || !to) return;

    const pidStr = this.contextProjectId().trim();
    const userNum = Number(empId);
    const projectNum = Number(pidStr);
    const usePmPendingApproval =
      pidStr.length > 0 && Number.isFinite(userNum) && Number.isFinite(projectNum);
    const useSupportRmPending =
      this.reportingManagerRosterMode() && Number.isFinite(userNum) && userNum > 0;

    const entries$ = useSupportRmPending
      ? this.supportPendingApprovalEntries$(userNum, from, to, empId)
      : usePmPendingApproval
        ? this.api.postPendingApprovalTimesheet(
            this.buildPendingApprovalPostBody(userNum, from, to, projectNum),
          )
        : this.api.getTimesheetEntries(empId, from, to).pipe(
            map((r) => ({ entries: r.entries, dayStatuses: {} as Record<string, ApprovalStatus> })),
          );

    // Unreported panel is hidden on Approval Timesheet; skip unreported-entries API entirely.
    if (this.reportingManagerRosterMode() || this.shell.approvalShellMode()) {
      this.unreportedApps.update((u) => ({ ...u, [empId]: [] }));
    } else if (!useSupportRmPending) {
      const unreported$ = usePmPendingApproval
        ? this.api.getUnreportedEntriesForUser(userNum, from, to)
        : this.api.getUnreportedApps(empId, from, to);
      unreported$.pipe(take(1)).subscribe({
        next: (unreported) => {
          this.unreportedApps.update((u) => ({ ...u, [empId]: unreported.apps }));
        },
      });
    } else {
      this.unreportedApps.update((u) => ({ ...u, [empId]: [] }));
    }

    const ctxPid = this.contextProjectId().trim();
    const assigned$ =
      ctxPid.length > 0 && empId.trim().length > 0
        ? this.api.getAssignedTasks(ctxPid, empId.trim())
        : of({ tasks: [] });

    const dateSet = new Set(dates);
    forkJoin({ pending: entries$, assigned: assigned$ })
      .pipe(take(1))
      .subscribe({
        next: ({ pending, assigned }) => {
          const enriched = pmEnrichEntriesWithTaskBoardBillable(
            pending.entries as UiTimesheetEntry[],
            assigned.tasks,
          );
          this.entries.update((m) => ({ ...m, [empId]: enriched }));
          this.dayStatus.update((s) => ({
            ...s,
            [empId]: { ...pending.dayStatuses },
          }));
          this.histLog.update((h) => h.filter((x) => x.empId !== empId || !dateSet.has(x.dateStr)));
          this.assignedTasks.update((a) => ({ ...a, [empId]: assigned.tasks }));
        },
      });
  }

  selectEmployee(id: string): void {
    this.selectedEmpId.set(id);
    this.loadEmployeeData(id);
  }

  navigateDate(dir: -1 | 1): void {
    const cur = parseIsoDate(this.curDate());
    const delta = this.curView() === 'weekly' ? 7 * dir : dir;
    this.curDate.set(toIsoDate(addDays(cur, delta)));
    this.reloadAllVisibleEmployees();
  }

  goToday(): void {
    this.curDate.set(this.today());
    this.reloadAllVisibleEmployees();
  }

  /**
   * Topbar calendar: jump to a chosen local day. Weekly view shows Mon–Sun for the week containing that day.
   */
  setAnchorFromCalendar(d: Date): void {
    const t = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (Number.isNaN(t.getTime())) return;
    this.curDate.set(toIsoDate(t));
    this.reloadAllVisibleEmployees();
  }

  private reloadAllVisibleEmployees(): void {
    if (this.reportingManagerRosterMode()) {
      this.fetchReportingManagerRoster();
    } else if (this.contextProjectId()) {
      this.fetchTeamMembers();
    } else {
      this.teamMembers().forEach((e) => this.loadEmployeeData(e.id));
    }
  }

  switchView(mode: ViewMode): void {
    this.curView.set(mode);
    this.reloadAllVisibleEmployees();
  }

  setFilter(tab: FilterTab): void {
    this.activeFilter.set(tab);
    const selected = this.selectedEmpId();
    if (selected) this.loadEmployeeData(selected);
  }

  approveDay(empId: string, dateStr: string, note: string, pmId: string): void {
    if (!this.canPmApproveOrRejectDate(dateStr)) {
      this.snack.show('Unavailable', 'This day cannot be approved.', HttpStatusCode.BadRequest);
      return;
    }
    const trimmed = note.trim();
    if (this.reportingManagerRosterMode()) {
      const userNum = Number(empId);
      const projectIds = this.resolveSupportApprovalProjectIds(empId, { dateStr });
      if (!Number.isFinite(userNum) || userNum <= 0 || projectIds.length === 0) {
        this.snack.show(
          'Error',
          'Could not resolve support project for this approval.',
          HttpStatusCode.BadRequest,
        );
        return;
      }
      this.postSupportApprovalStatusByRangeForProjects(
        projectIds,
        userNum,
        dateStr,
        dateStr,
        'approved',
        trimmed,
      )
        .pipe(take(1))
        .subscribe((result) => {
          if (!result.success) {
            this.showApprovalMutationSnack(result, 'Approved');
            return;
          }
          this.dayStatus.update((s) => ({
            ...s,
            [empId]: { ...s[empId], [dateStr]: 'approved' },
          }));
          this.recordApprovalHistory(empId, [dateStr], 'approved', trimmed);
          this.afterPmApprovalMutation(empId);
          this.showApprovalMutationSnack(result, 'Approved');
        });
      return;
    }
    const projectNum = this.parsePositiveProjectId(this.contextProjectId());
    if (projectNum != null) {
      const userNum = Number(empId);
      if (!Number.isFinite(userNum) || userNum <= 0) {
        this.snack.show('Error', 'Invalid employee id.', HttpStatusCode.BadRequest);
        return;
      }
      this.api
        .postProjectTimesheetApprovalStatusByRange({
          userId: userNum,
          projectId: projectNum,
          startDate: dateStr,
          endDate: dateStr,
          approvalStatus: 'approved',
          remark: trimmed,
        })
        .pipe(take(1))
        .subscribe((result) => {
          if (!result.success) {
            this.showApprovalMutationSnack(result, 'Approved');
            return;
          }
          this.dayStatus.update((s) => ({
            ...s,
            [empId]: { ...s[empId], [dateStr]: 'approved' },
          }));
          this.recordApprovalHistory(empId, [dateStr], 'approved', trimmed);
          this.afterPmApprovalMutation(empId);
          this.showApprovalMutationSnack(result, 'Approved');
        });
      return;
    }
    this.api
      .approveDay({ empId, dateStr, note: trimmed, pmId })
      .pipe(
        take(1),
        catchError((err) =>
          of(
            this.approvalResultFromHttpError(err, 'Approval request failed.') as {
              success: boolean;
              updatedStatus: 'approved';
              message?: string;
            },
          ),
        ),
      )
      .subscribe((r) => {
        const result = this.approvalResultFromLegacyResponse(
          r,
          'Timesheet approved successfully.',
          'Approval request failed.',
        );
        if (!result.success) {
          this.showApprovalMutationSnack(result, 'Approved');
          return;
        }
        this.dayStatus.update((s) => ({
          ...s,
          [empId]: { ...s[empId], [dateStr]: 'approved' },
        }));
        this.recordApprovalHistory(empId, [dateStr], 'approved', trimmed);
        this.afterPmApprovalMutation(empId);
        this.showApprovalMutationSnack(result, 'Approved');
      });
  }

  /**
   * POST `approval-status-by-range` for the selected employee, visible week range, and all currently pending days.
   */
  applyBulkApprovalStatusForVisibleRange(
    approvalStatus: 'approved' | 'rejected',
    remark: string,
  ): Observable<TimesheetApprovalMutationResult> {
    const empId = this.selectedEmpId();
    const userNum = Number(empId);
    const visible = this.visibleDates();
    const from = visible[0];
    const to = visible[visible.length - 1];
    const pendingDates = [...this.pendingSubmittedDates()];
    const trimmed = remark.trim();
    if (
      !empId ||
      pendingDates.length === 0 ||
      this.isVisibleWeekBeforePmApprovalCutoff() ||
      !from ||
      !to ||
      !Number.isFinite(userNum) ||
      userNum <= 0
    ) {
      return of({ success: false, message: 'No pending timesheets in this range to update.' });
    }

    const applyLocalBulkState = () => {
      if (approvalStatus === 'approved') {
        this.dayStatus.update((s) => {
          const next = { ...s };
          const map = { ...(next[empId] ?? {}) };
          for (const d of pendingDates) map[d] = 'approved';
          next[empId] = map;
          return next;
        });
        this.recordApprovalHistory(
          empId,
          pendingDates,
          'approved',
          trimmed || 'Week approved',
        );
      } else {
        this.dayStatus.update((s) => {
          const next = { ...s };
          const map = { ...(next[empId] ?? {}) };
          for (const d of pendingDates) map[d] = 'rejected';
          next[empId] = map;
          return next;
        });
        this.rejMeta.update((m) => {
          const next = { ...m };
          for (const d of pendingDates) {
            const prev = m[`${empId}__${d}`]?.count ?? 0;
            next[`${empId}__${d}`] = { count: prev + 1, note: trimmed };
          }
          return next;
        });
        this.recordApprovalHistory(empId, pendingDates, 'rejected', trimmed);
      }
      this.afterPmApprovalMutation(empId);
    };

    if (this.reportingManagerRosterMode()) {
      const projectIds = this.resolveSupportApprovalProjectIds(empId, {
        pendingDates,
      });
      if (projectIds.length === 0) {
        return of({ success: false, message: 'Could not resolve support project for approval.' });
      }
      return this.postSupportApprovalStatusByRangeForProjects(
        projectIds,
        userNum,
        from,
        to,
        approvalStatus,
        trimmed,
      ).pipe(
        tap((result) => {
          if (result.success) applyLocalBulkState();
        }),
        take(1),
      );
    }

    const projectNum = this.parsePositiveProjectId(this.contextProjectId());
    if (projectNum == null) {
      return of({
        success: false,
        message: 'Project context is missing. Open timesheet approval from a project.',
      });
    }
    return this.api
      .postProjectTimesheetApprovalStatusByRange({
        userId: userNum,
        projectId: projectNum,
        startDate: from,
        endDate: to,
        approvalStatus,
        remark: trimmed,
      })
      .pipe(
        tap((result) => {
          if (result.success) applyLocalBulkState();
        }),
        take(1),
      );
  }

  rejectDay(empId: string, dateStr: string, reason: string, pmId: string): void {
    if (!this.canPmApproveOrRejectDate(dateStr)) {
      this.snack.show('Unavailable', 'This day cannot be rejected.', HttpStatusCode.BadRequest);
      return;
    }
    const trimmed = reason.trim();
    const currentCount = this.getRejCount(empId, dateStr);
    const applyRejectState = (rejectionCount: number) => {
      this.dayStatus.update((s) => ({
        ...s,
        [empId]: { ...s[empId], [dateStr]: 'rejected' },
      }));
      this.rejMeta.update((m) => ({
        ...m,
        [`${empId}__${dateStr}`]: { count: rejectionCount, note: trimmed },
      }));
      this.recordApprovalHistory(empId, [dateStr], 'rejected', trimmed);
      this.afterPmApprovalMutation(empId);
    };
    if (this.reportingManagerRosterMode()) {
      const userNum = Number(empId);
      const projectIds = this.resolveSupportApprovalProjectIds(empId, { dateStr });
      if (!Number.isFinite(userNum) || userNum <= 0 || projectIds.length === 0) {
        this.snack.show(
          'Error',
          'Could not resolve support project for this rejection.',
          HttpStatusCode.BadRequest,
        );
        return;
      }
      this.postSupportApprovalStatusByRangeForProjects(
        projectIds,
        userNum,
        dateStr,
        dateStr,
        'rejected',
        trimmed,
      )
        .pipe(take(1))
        .subscribe((result) => {
          if (!result.success) {
            this.showApprovalMutationSnack(result, 'Rejected');
            return;
          }
          applyRejectState(currentCount + 1);
          this.showApprovalMutationSnack(result, 'Rejected');
        });
      return;
    }
    const projectNum = this.parsePositiveProjectId(this.contextProjectId());
    if (projectNum != null) {
      const userNum = Number(empId);
      if (!Number.isFinite(userNum) || userNum <= 0) {
        this.snack.show('Error', 'Invalid employee id.', HttpStatusCode.BadRequest);
        return;
      }
      this.api
        .postProjectTimesheetApprovalStatusByRange({
          userId: userNum,
          projectId: projectNum,
          startDate: dateStr,
          endDate: dateStr,
          approvalStatus: 'rejected',
          remark: trimmed,
        })
        .pipe(take(1))
        .subscribe((result) => {
          if (!result.success) {
            this.showApprovalMutationSnack(result, 'Rejected');
            return;
          }
          applyRejectState(currentCount + 1);
          this.showApprovalMutationSnack(result, 'Rejected');
        });
      return;
    }
    this.api
      .rejectDay({ empId, dateStr, reason: trimmed, pmId, currentCount })
      .pipe(
        take(1),
        catchError((err) =>
          of(
            this.approvalResultFromHttpError(err, 'Rejection request failed.') as {
              success: boolean;
              updatedStatus: 'rejected';
              rejectionCount: number;
              message?: string;
            },
          ),
        ),
      )
      .subscribe((res) => {
        const result = this.approvalResultFromLegacyResponse(
          res,
          'Timesheet rejected successfully.',
          'Rejection request failed.',
        );
        if (!result.success) {
          this.showApprovalMutationSnack(result, 'Rejected');
          return;
        }
        applyRejectState(res.rejectionCount);
        this.showApprovalMutationSnack(result, 'Rejected');
      });
  }

  /** Numeric project id for manager-review: modal override → route context → row `projectId`. */
  private resolveManagerReviewProjectId(
    entries: UiTimesheetEntry[],
    projectIdOverride?: string,
  ): number | null {
    const tryPid = (raw: string | undefined): number | null => {
      const t = (raw ?? '').trim();
      if (!t.length) return null;
      const n = Number(t);
      return Number.isFinite(n) && n > 0 ? n : null;
    };
    const fromOverride = tryPid(projectIdOverride);
    if (fromOverride != null) return fromOverride;
    const fromCtx = tryPid(this.contextProjectId());
    if (fromCtx != null) return fromCtx;
    for (const e of entries) {
      const n = tryPid(String(e.projectId ?? ''));
      if (n != null) return n;
    }
    return null;
  }

  /** CRM `task_id` for manager-review `hourEdits.taskId`. */
  private taskIdForHourEdit(e: UiTimesheetEntry): number | null {
    const taskLine = e.taskId;
    if (taskLine != null && Number.isFinite(taskLine) && taskLine > 0) {
      return Math.trunc(taskLine);
    }
    return null;
  }

  /** Line `detail_id` for manager-review `hourEdits.detailId`. */
  private detailIdForHourEdit(e: UiTimesheetEntry): number | null {
    const detail = e.timesheetDetailId;
    if (detail != null && Number.isFinite(detail) && detail > 0) {
      return Math.trunc(detail);
    }
    return null;
  }

  /**
   * Deduplicate hour edits; last value wins.
   * Prefer `detailId` as the merge key so sibling lines that share a CRM task stay distinct.
   */
  private mergeHourEditsForReview(edits: ProjectTimesheetHourEdit[]): ProjectTimesheetHourEdit[] {
    const m = new Map<string, ProjectTimesheetHourEdit>();
    for (const x of edits) {
      const key =
        x.detailId != null && x.detailId > 0
          ? `d:${x.detailId}`
          : `t:${x.taskId}`;
      m.set(key, {
        taskId: x.taskId,
        ...(x.detailId != null && x.detailId > 0 ? { detailId: x.detailId } : {}),
        loggedHours: x.loggedHours,
      });
    }
    return [...m.values()];
  }

  private hoursChangedForReview(current: UiTimesheetEntry, original: UiTimesheetEntry | undefined): boolean {
    if (!original) return true;
    const cur = Number(current.hours);
    const orig = Number(original.hours);
    const curN = Number.isFinite(cur) ? cur : 0;
    const origN = Number.isFinite(orig) ? orig : 0;
    return curN !== origN;
  }

  editTimesheet(
    empId: string,
    dates: string[],
    entries: UiTimesheetEntry[],
    _pmId: string,
    projectIdOverride?: string,
    /** Modal open snapshot — when provided, only hour-changed lines are sent in `hourEdits`. */
    originalEntries?: readonly UiTimesheetEntry[],
  ): Observable<boolean> {
    const projectId = this.resolveManagerReviewProjectId(entries, projectIdOverride);
    if (projectId == null) {
      this.snack.show(
        'Error',
        'Missing project id for save. Open the PM timesheet from a project with a valid projectId.',
        HttpStatusCode.BadRequest,
      );
      return of(false);
    }
    const originalById = new Map((originalEntries ?? []).map((e) => [e.id, e]));
    const groupEdits = new Map<number, ProjectTimesheetHourEdit[]>();
    /** All timesheet ids in scope (approve even when some days have no hour edits). */
    const timesheetIds = new Set<number>();

    for (const e of entries.filter((x) => !x._deleted)) {
      const ts = e.timesheetId != null && e.timesheetId > 0 ? Math.trunc(e.timesheetId) : null;
      if (ts == null) continue;
      timesheetIds.add(ts);

      const onlyDirty = originalEntries != null;
      if (onlyDirty && !this.hoursChangedForReview(e, originalById.get(e.id))) {
        continue;
      }

      const taskId = this.taskIdForHourEdit(e);
      if (taskId == null) continue;
      const detailId = this.detailIdForHourEdit(e);
      const hrs = Number(e.hours);
      const arr = groupEdits.get(ts) ?? [];
      arr.push({
        taskId,
        ...(detailId != null ? { detailId } : {}),
        loggedHours: Number.isFinite(hrs) ? hrs : 0,
      });
      groupEdits.set(ts, arr);
    }

    if (timesheetIds.size === 0) {
      this.snack.show(
        'Error',
        'No valid timesheet/task rows found to approve. Pending-approval data must include timesheet_id and task_id.',
        HttpStatusCode.BadRequest,
      );
      return of(false);
    }

    const timesheets = [...timesheetIds].map((timesheetId) => ({
      timesheetId,
      hourEdits: this.mergeHourEditsForReview(groupEdits.get(timesheetId) ?? []),
    }));
    if (timesheets.length === 0) {
      this.snack.show('Error', 'Nothing to save after grouping edits.', HttpStatusCode.BadRequest);
      return of(false);
    }

    return this.api
      .postProjectTimesheetManagerReview(projectId, {
        approvalStatus: 'approved',
        remark: 'Hours corrected and approved',
        timesheets,
      })
      .pipe(
        tap((ok) => {
          if (!ok) {
            this.snack.show(
              'Error',
              'The server rejected timesheet update (success: false).',
              HttpStatusCode.BadRequest,
            );
            return;
          }
          this.dayStatus.update((s) => {
            const next = { ...s };
            const map = { ...(next[empId] ?? {}) };
            for (const d of dates) map[d] = 'approved';
            next[empId] = map;
            return next;
          });
          this.histLog.update((h) => [
            ...h,
            {
              id: `local_${Date.now()}`,
              empId,
              dateStr: dates[0] ?? '',
              action: 'approved',
              actorName: 'Current PM',
              actorRole: 'PM',
              timestamp: new Date().toISOString(),
              note: 'Hours corrected and approved',
            },
          ]);
          this.afterPmApprovalMutation(empId);
          this.snack.show('Success', 'Timesheet updated and approved.', HttpStatusCode.Ok);
        }),
        catchError((err: { error?: { message?: string } }) => {
          const msg =
            err?.error?.message ??
            'Could not reach project-timesheet manager-review. Check API URL and network.';
          this.snack.show('Error', msg, HttpStatusCode.BadRequest);
          return of(false);
        }),
        take(1),
      );
  }

  getDayStatus(empId: string, dateStr: string): ApprovalStatus {
    return this.dayStatus()[empId]?.[dateStr] ?? 'draft';
  }

  getRejCount(empId: string, dateStr: string): number {
    return this.rejMeta()[`${empId}__${dateStr}`]?.count ?? 0;
  }

  getRejNote(empId: string, dateStr: string): string {
    return this.rejMeta()[`${empId}__${dateStr}`]?.note ?? '';
  }

  isRejLimitReached(empId: string, dateStr: string): boolean {
    return this.getRejCount(empId, dateStr) >= this.REJECTION_LIMIT;
  }

  isBeforeTimesheetMinDate(dateStr: string): boolean {
    return dateStr < TIMESHEET_FILL_MIN_DATE_STR;
  }

  weekMondayStr(dateStr: string): string {
    return toIsoDate(startOfWeekMonday(parseIsoDate(dateStr)));
  }

  isWeekBeforePmApprovalCutoffForDate(dateStr: string): boolean {
    return this.weekMondayStr(dateStr) < PM_APPROVAL_WEEK_MIN_MONDAY_STR;
  }

  /** PM may approve/reject this day (not future, on/after 8 May 2026, week on/after 4 May 2026). */
  canPmApproveOrRejectDate(dateStr: string): boolean {
    if (this.isDateFuture(dateStr)) return false;
    if (this.isBeforeTimesheetMinDate(dateStr)) return false;
    if (this.isWeekBeforePmApprovalCutoffForDate(dateStr)) return false;
    return true;
  }

  pmApprovalBlockedTitle(): string | null {
    if (this.isVisibleWeekBeforePmApprovalCutoff()) {
      return 'Approval is not available for weeks before 4 May 2026.';
    }
    return null;
  }

  isDateFuture(dateStr: string): boolean {
    return dateStr > this.today();
  }

  getAssignedWithStatus(empId: string): UiAssignedTask[] {
    const tasks = this.assignedTasks()[empId] ?? [];
    const ent = this.entries()[empId] ?? [];
    const todayStr = this.today();
    return tasks.map((t) => {
      const logged = ent.filter((e) => e.task === t.task).reduce((s, e) => s + e.hours, 0);
      let status: TaskStatus;
      if (t.endDate && todayStr > t.endDate) {
        status = logged >= t.plannedHours ? 'completed' : 'delayed';
      } else if (t.startDate && todayStr < t.startDate) {
        status = 'yet-to-start';
      } else {
        status = logged > 0 ? 'ongoing' : 'yet-to-start';
      }
      return { ...t, loggedHours: logged, status };
    });
  }

  private enrichEmployee(e: ApiEmployee): UiEmployee {
    const dates = this.visibleDates();
    const statusMap = this.dayStatus()[e.id] ?? {};
    let pendingCount = 0;
    let approvedCount = 0;
    let rejectedCount = 0;
    for (const d of dates) {
      const s = statusMap[d];
      if (s === 'submitted') pendingCount++;
      else if (s === 'approved') approvedCount++;
      else if (s === 'rejected') rejectedCount++;
    }
    return {
      ...e,
      pendingCount,
      approvedCount,
      rejectedCount,
    };
  }
}